import { NavigableButton } from '@carvana/ds-button';
import { InventoryTypeKey } from '@carvana/merch-contracts/InventoryType';
import abbreviateMileage from '@carvana/merch-tile/utils/abbreviateMileage';
import { useMemo } from 'react';
import StatusCardType from '@/common/helios/OrderStatusCard/StatusCardType';
import {
  getCtaLink,
  getCtaText,
  getCustomAttributesDescription,
  getImageSrc,
  getSubText,
  getTitleText
} from '@/common/helios/OrderStatusCard/utils';
import { RemainingTasks } from '@/common/helios/OrderStatusCard/VerificationTasks';
import TrackWrapper from '@/common/TrackWrapper';
import OrderState from '~/constants/common/OrderState';
import { useIsMobile } from '~/context/common/DeviceContext';
import { useConsumer } from '~/context/common/StaticContext';
import { PurchaseEventPayload } from '~/native/common/contracts';
import { useSourcedHandleNativePurchaseOnClick } from '~/native/common/hooks';
import {
  getNativeVdpPayload,
  nativeMyCarsPayload,
  nativeOpdPayload
} from '~/native/common/utils';
import defaultImageHandler from '~/utilities/defaultImageHandler';
import formatEvent from '~/utilities/formatEvent';
import {
  Card,
  CopySection,
  ImageContainer,
  ImageLink,
  SubTitle,
  Title,
  TrimMileage,
  VehicleData,
  VehicleImage,
  VehicleName,
  YMMTWrapper
} from './OrderStatusCard.styles';

type OwnProps = {
  cardStatus: StatusCardType;
  openVerifications?: {
    hasOpenVerifications: boolean;
    openVerificationsCount: number;
  };
  orderState: OrderState;
  vehicle?: {
    imageUrl: string;
    inventoryType: InventoryTypeKey;
    make: string;
    mileage: number;
    model: string;
    stockNumber: number;
    trim: string;
    vehicleId: number;
    year: number;
  };
};

export default function OrderStatusCard({
  cardStatus,
  openVerifications,
  orderState,
  vehicle
}: OwnProps) {
  const consumer = useConsumer();
  const isMobile = useIsMobile();
  const handleNativePurchaseOnClick = useSourcedHandleNativePurchaseOnClick();

  const nativePayload = useMemo(() => {
    switch (cardStatus) {
      case StatusCardType.PurchaseInProgress:
        return '';
      case StatusCardType.OrderPlaced:
        return nativeOpdPayload;
      case StatusCardType.Purchased:
        return nativeMyCarsPayload;
      case StatusCardType.Reserved:
        return vehicle?.vehicleId != null
          ? getNativeVdpPayload(vehicle.vehicleId)
          : '';
      default:
        return '';
    }
  }, [cardStatus, vehicle?.vehicleId]);

  if (
    !vehicle ||
    (cardStatus === StatusCardType.OrderPlaced && !openVerifications)
  ) {
    return null;
  }

  const {
    imageUrl,
    inventoryType,
    make,
    mileage = 0,
    model,
    stockNumber,
    trim,
    vehicleId,
    year
  } = vehicle;

  const customAttributes = {
    description: getCustomAttributesDescription(cardStatus),
    vehicleId
  };

  const hasOpenVerifications = openVerifications?.hasOpenVerifications ?? false;

  const title = getTitleText(cardStatus, make, hasOpenVerifications);
  const subText = getSubText(cardStatus, hasOpenVerifications, orderState);
  const ctaText = getCtaText(cardStatus);
  const ctaLink = getCtaLink(cardStatus, vehicleId, consumer);

  const sendContinuePurchaseNativePayload =
    cardStatus === StatusCardType.PurchaseInProgress;

  const shouldShowRemainingTasks =
    cardStatus === StatusCardType.OrderPlaced && hasOpenVerifications;

  const nativePurchaseEventPayload: PurchaseEventPayload = {
    make,
    model,
    trim,
    year,
    vehicleId,
    vehicleImage: imageUrl,
    inventoryType,
    stockNumber
  };

  const sharedCtaProps = {
    'data-analytics-id': 'Home feed card',
    'data-analytics-attributes': JSON.stringify(customAttributes),
    'data-native-included': !sendContinuePurchaseNativePayload,
    'data-native-payload': nativePayload,
    href: ctaLink,
    onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (sendContinuePurchaseNativePayload) {
        handleNativePurchaseOnClick(event, nativePurchaseEventPayload);
      }
    }
  };

  return (
    <TrackWrapper
      event={formatEvent('Home feed card', {
        action: 'viewed',
        description: 'Order Status Card',
        status: StatusCardType[cardStatus],
        vehicleId
      })}
      className="z-1 rounded-md shadow-md sm:col-span-2 md:col-span-3"
    >
      <Card data-testid="purchase-in-progress-card">
        <CopySection>
          <Title data-testid="purchase-in-progress-title">{title}</Title>
          {shouldShowRemainingTasks ? (
            <a data-testid="order-status-remaining-tasks" {...sharedCtaProps}>
              <RemainingTasks
                hasOpenVerifications={hasOpenVerifications}
                openVerificationsCount={
                  openVerifications?.openVerificationsCount ?? 0
                }
              />
            </a>
          ) : (
            subText && (
              <SubTitle data-testid="purchase-in-progress-text">
                {subText}
              </SubTitle>
            )
          )}
        </CopySection>

        <ImageLink
          {...sharedCtaProps}
          aria-hidden="true"
          tabIndex={-1}
          data-testid="Status Card Image CTA"
        >
          <ImageContainer>
            <VehicleImage
              alt={`${make} ${model}`}
              src={getImageSrc({
                imageUrl,
                imageWidth: 700,
                inventoryType,
                stockNumber
              })}
              onError={defaultImageHandler}
              data-testid="purchase-in-progress-image"
            />
          </ImageContainer>
        </ImageLink>

        <VehicleData>
          <YMMTWrapper>
            <VehicleName data-testid="purchase-in-progress-year-make-model">
              {year} {make} {model}
            </VehicleName>
            <TrimMileage data-testid="purchase-in-progress-trim">
              <span className="truncate">{trim}</span>
              {mileage ? (
                <>
                  <span className="hidden sm:inline">·</span>
                  <span>{abbreviateMileage(mileage)} miles</span>
                </>
              ) : null}
            </TrimMileage>
          </YMMTWrapper>

          <NavigableButton
            {...sharedCtaProps}
            variant="primary"
            size={isMobile ? 'sm' : 'md'}
            className="w-full capitalize lg:w-[360px]"
            data-testid="Status Card Btn CTA"
          >
            {ctaText}
          </NavigableButton>
        </VehicleData>
      </Card>
    </TrackWrapper>
  );
}
